import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { capitalizeFirstLetter, formatUTCToUserLocal, PUSH_NOTIFICATION_MODULE, SUBSCRIPTION_MODULE_TYPE, SUPPORTED_PAYMENT_METHOD, SUPPORTED_PAYMENT_METHOD_NAME } from "../../constants";
import Currency from "../currency";
import CurrencyFormat from "../currencyFormat/currencyFormat";
import { getPlanPaidDetailsAction, paymentLinkStateAction } from "../../actions/stripe";
import { RoutesPaths } from "../../Router/constant";
import PaymentStatusSkeleton from "./PaymentStatusSkeleton";
import { createChat } from "../../services/api";
import { useLocation, useNavigate } from "react-router";
import { logoutChannel } from "../../actions/auth";

const ACTIVATE_BUSINESS_LOGOUT_URL = "https://staging.kimbocorp.com/activate-your-business/";

export default function PaymentStatus({ planId, paymentId, isAdvisor = false, isStep = false, isActivationFlow = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentResponse, paymentBankModalMessage, paymentResponseLoader, paymentLinkState } = useSelector(state => state.stripe)
  const chatCreatedRef = useRef(false);
  const shareInProgressRef = useRef(false);
  const [logoutRedirecting, setLogoutRedirecting] = useState(false);
  const queryPaymentId = new URLSearchParams(location.search).get("paymentid");
  const selectedPaymentId = paymentId || queryPaymentId;

  useEffect(() => {
    dispatch(getPlanPaidDetailsAction(planId, selectedPaymentId));
  }, [dispatch, planId, selectedPaymentId]);


  const { payment, plan } = paymentResponse || {};
  const paidByName =
    `${payment?.user?.first_name || ''} ${payment?.user?.last_name || ''}`.trim() || '-';
  const isPaymentCompleted =
    payment?.payment_status?.includes('approved') ||
    payment?.payment_status?.includes('paid');
  const isCustomPlan = plan?.planType?.value === SUBSCRIPTION_MODULE_TYPE.CUSTOM;
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}${RoutesPaths.PUBLIC_PAYMENT_STATUS.replace(":slug", plan?.slug || "")}${selectedPaymentId ? `?paymentid=${selectedPaymentId}` : ""}`
    : `${RoutesPaths.PUBLIC_PAYMENT_STATUS.replace(":slug", plan?.slug || "")}${selectedPaymentId ? `?paymentid=${selectedPaymentId}` : ""}`;
  const paymentStatusMessage = payment?.payment_status?.includes('failed')
    ? "Payment failed"
    : isPaymentCompleted
      ? "Payment successful"
      : payment?.payment_status
        ? "Payment pending"
        : paymentBankModalMessage;

  useEffect(() => {
    const shouldCreateChat =
      paymentLinkState?.pageType !== "chat" ||
      isCustomPlan;

    const createPaymentChat = async () => {
      chatCreatedRef.current = true;

      const innerValue = `Payment done`;
      const attributes = {
        companyId: plan?.company,
        module: PUSH_NOTIFICATION_MODULE.PAYMENTLINKPAID,
        data: paymentResponse,
        participants: [plan?.createdBy?._id],
        message: innerValue,
        quotationId: plan?.id || plan?._id,
      };

      await createChat(attributes);
    };

    if (
      isStep &&
      shouldCreateChat &&
      (isPaymentCompleted || isCustomPlan) &&
      plan?.createdBy?._id &&
      !chatCreatedRef.current
    ) {
      createPaymentChat();
    }
  }, [isCustomPlan, isPaymentCompleted, paymentLinkState, paymentResponse, plan, isStep]);

  const handleShare = async () => {
    if (shareInProgressRef.current) {
      return;
    }

    try {
      shareInProgressRef.current = true;
      if (navigator.share && (!navigator.canShare || navigator.canShare({ url: shareUrl }))) {
        await navigator.share({
          title: `Hey! Here's the payment link for ${plan?.name}`,
          url: shareUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        alert("Share link copied to clipboard.");
        return;
      }

      window.prompt("Copy this share link", shareUrl);
    } catch (err) {
      if (err?.name === "AbortError") {
        return;
      }

      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          alert("Share link copied to clipboard.");
          return;
        } catch (clipboardError) {
          console.log("Clipboard fallback failed:", clipboardError);
        }
      }

      console.log("Share failed:", err);
      window.prompt("Copy this share link", shareUrl);
    } finally {
      shareInProgressRef.current = false;
    }
  };

  const onClose = async () => {
    navigate(RoutesPaths.MESSAGE_DETAIL_PUBLIC_CHAT.replace(":id", plan?.createdBy?._id));
    dispatch(paymentLinkStateAction(null))
  };

  const handleActivationLogout = async () => {
    if (logoutRedirecting) return;

    setLogoutRedirecting(true);
    logoutChannel.postMessage("logout");
    localStorage.removeItem("kimbocorp");
    localStorage.removeItem("token");
    localStorage.removeItem("selectedCompany");
    localStorage.removeItem("userGuidePage");
    window.location.replace(ACTIVATE_BUSINESS_LOGOUT_URL);
  };

  return (
    <>
      <div className="container p-2">
        <div className="buyProductSectionBgPayout">
          {paymentResponseLoader ?
            <PaymentStatusSkeleton />
            :
            <div className="">
              <div className="transferAmount">
                {payment?.payment_status?.includes('approved') || payment?.payment_status?.includes('paid') ?
                  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="36" cy="36" r="36" fill="#23A26D" fill-opacity="0.12" />
                    <path d="M36.4987 19.333C27.3154 19.333 19.832 26.8163 19.832 35.9997C19.832 45.183 27.3154 52.6663 36.4987 52.6663C45.682 52.6663 53.1654 45.183 53.1654 35.9997C53.1654 26.8163 45.682 19.333 36.4987 19.333ZM44.4654 32.1663L35.0154 41.6163C34.782 41.8497 34.4654 41.983 34.132 41.983C33.7987 41.983 33.482 41.8497 33.2487 41.6163L28.532 36.8997C28.0487 36.4163 28.0487 35.6163 28.532 35.133C29.0154 34.6497 29.8154 34.6497 30.2987 35.133L34.132 38.9663L42.6987 30.3997C43.182 29.9163 43.982 29.9163 44.4654 30.3997C44.9487 30.883 44.9487 31.6663 44.4654 32.1663Z" fill="#23A26D" />
                  </svg>
                  : payment?.payment_status?.includes('failed') ?
                    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="36" r="36" fill="#FEF2F2" />
                      <rect x="16.5" y="16" width="40" height="40" rx="20" fill="#9C1515" />
                      <g clip-path="url(#clip0_17433_8795)">
                        <path d="M39.0013 33.5003L34.0013 38.5003M34.0013 33.5003L39.0013 38.5003M44.8346 36.0003C44.8346 40.6027 41.1037 44.3337 36.5013 44.3337C31.8989 44.3337 28.168 40.6027 28.168 36.0003C28.168 31.398 31.8989 27.667 36.5013 27.667C41.1037 27.667 44.8346 31.398 44.8346 36.0003Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_17433_8795">
                          <rect width="20" height="20" fill="white" transform="translate(26.5 26)" />
                        </clipPath>
                      </defs>
                    </svg>
                    :
                    <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="36" cy="36" r="36" fill="#FACC15" fill-opacity="0.1" />
                      <rect x="16.5" y="16" width="40" height="40" rx="20" fill="#FACC15" />
                      <g clip-path="url(#clip0_17433_8596)">
                        <path d="M36.5013 39.3337V36.0003M36.5013 32.667H36.5096M44.8346 36.0003C44.8346 40.6027 41.1037 44.3337 36.5013 44.3337C31.8989 44.3337 28.168 40.6027 28.168 36.0003C28.168 31.398 31.8989 27.667 36.5013 27.667C41.1037 27.667 44.8346 31.398 44.8346 36.0003Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_17433_8596">
                          <rect width="20" height="20" fill="white" transform="translate(26.5 26)" />
                        </clipPath>
                      </defs>
                    </svg>
                }
              </div>
              <div className="paymentPersonName">
                {/* <h4>{paymentBankModalMessage}</h4> */}
                <p>{payment?.paymentMethodSelection === SUPPORTED_PAYMENT_METHOD.BANK_TRANSFER ? "Transfer initiated" : paymentBankModalMessage}</p>
              </div>

              <div className="paymentStatusHolder">
                <h5>Payment details</h5>
                <div className="paymentStatusHeader">
                  <div className="rowHolder">
                    <p>Amount</p>
                    <p className="amountText">
                      <Currency
                        currency={payment?.currency || ""}
                      />
                      <CurrencyFormat
                        currency={payment?.currency}
                        value={parseFloat(payment?.amount / 100)}
                        decimal={2}
                      />
                    </p>
                  </div>
                  <div className="rowHolder m-0">
                    <p>Status</p>
                    <div className={`statusBadge ${payment?.payment_status?.includes('approved') || payment?.payment_status?.includes('paid') ? "" : "notPaid"}`}><p>{capitalizeFirstLetter(payment?.payment_status)}</p></div>
                  </div>
                </div>
                <div>
                  <div className="rowHolder">
                    <p>Payment time</p>
                    <p className="rightText">{formatUTCToUserLocal(payment?.createdAt)}</p>
                  </div>
                  <div className="rowHolder">
                    <p>Transaction ID</p>
                    <p className="rightText">{payment?.paymentId}</p>
                  </div>
                  {plan?.planType?.value === SUBSCRIPTION_MODULE_TYPE.CUSTOM &&
                    <div className="rowHolder">
                      <p>Quotation ID</p>
                      <p className="rightText">Quotation {plan?.quotationId}</p>
                    </div>
                  }
                  <div className="rowHolder">
                    <p>Payment method</p>
                    <p className="rightText">{SUPPORTED_PAYMENT_METHOD_NAME[payment?.paymentMethodSelection]}</p>
                  </div>
                  <div className="rowHolder">
                    <p>Paid by</p>
                    <p className="rightText">{paidByName}</p>
                  </div>
                </div>
              </div>
            </div>
          }
          <div className="quotationstatusBanner">
            <p>
              {isActivationFlow
                ? "You can securely logout this session and log in to the kimbocorp app to see your business activity."
                : "Continue the conversation seamlessly on our mobile app for a smoother and more secure experience. Your temporary login credentials have been sent to your email, which you can use to access your account."}
            </p>
            {isActivationFlow && (
              <div className="paymentStatusActivationActions">
                <div className="quotationstatusBannerAppLink">
                  <a
                    href="https://apps.apple.com/us/app/kimbocorp/id6737228466"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src="assets/img/landingPage/applestore.svg" alt="Download on the App Store" />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=io.kimbocorp"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src="assets/img/landingPage/playstore.svg" alt="Get it on Google Play" />
                  </a>
                </div>
                <button
                  type="button"
                  className="paymentStatusLogoutButton"
                  onClick={handleActivationLogout}
                  disabled={logoutRedirecting}
                >
                  {logoutRedirecting ? "Logging out..." : "Logout"}
                </button>
              </div>
            )}
          </div>
        </div>
        {!isActivationFlow && (
          <div className="row mb-4">
            <div className={`col-4`}>
              <div
                type="button"
                onClick={handleShare}
                className={`gobackButton`}>
                <p className="m-0">Share</p>
              </div>
            </div>
            <div className={`col-8`}>
              <div
                onClick={onClose}
                style={{ backgroundColor: isAdvisor ? "#494DA2" : "#2978B5" }}
                className="gobackButton continueButton">
                <p className="m-0">Go back {paymentLinkState?.pageType === 'chat' ? "to chat" : ""}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
